/**
 * Accounting → Statements: the month-end statement for any month — open it,
 * download it (PDF through the print page, Excel from the server), close it.
 *
 * Closing stores the statement as it stands (migration 064); a closed month is
 * shown from that snapshot from then on, and "Changed since close" lists the
 * totals the live figures have moved since. Reopen deletes the snapshot.
 */

import React, { useEffect, useState } from 'react';
import { CRM } from '../lib/theme';
import { useApiClient } from '../hooks/useApi';
import { useAction } from '../hooks/useAction';
import { formatMoney, formatDate } from '../lib/utils';

const C = { ...CRM, mono: "'DM Mono', monospace" };

const STATUS: Record<string, { label: string; color: string; bg: string }> = {
  open: { label: 'Open', color: C.muted, bg: C.panel2 },
  closed: { label: 'Closed', color: C.green, bg: C.greenTint },
  changed: { label: 'Changed since close', color: C.amber, bg: C.amberTint },
};

const btn: React.CSSProperties = {
  padding: '7px 12px', background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '7px',
  color: C.text, cursor: 'pointer', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
};

export function StatementsTab() {
  const api = useApiClient();
  const act = useAction();
  const [list, setList] = useState<{ months: any[]; migrationMissing: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [month, setMonth] = useState<string>('');
  // Which report the View / Download buttons act on.
  const [report, setReport] = useState<'statement' | 'jobs'>('statement');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(null);
    api.getStatements()
      .then((r) => {
        if (!alive) return;
        const data = r.data ?? { months: [], migrationMissing: false };
        setList(data);
        // Default: the latest month that has ended.
        setMonth((m) => m || (data.months.find((x: any) => x.ended) ?? data.months[0])?.month || '');
      })
      .catch((e: any) => { if (alive) setError(e?.message || 'Could not load statements'); });
    return () => { alive = false; };
  }, [api, reload]);

  const months = list?.months ?? [];
  const sel = months.find((m) => m.month === month);

  const openPrint = (print: boolean) => {
    const q = [report === 'jobs' ? 'view=jobs' : '', print ? 'print=1' : ''].filter(Boolean).join('&');
    window.open(`/accounting/statement/${month}${q ? `?${q}` : ''}`, '_blank');
  };
  const downloadXlsx = () => act.run(async () => {
    const { blob, filename } = report === 'jobs' ? await api.downloadJobReviewXlsx(month) : await api.downloadStatementXlsx(month);
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 1000);
  }).catch((e: any) => window.alert(e?.message || 'Could not build the Excel file'));
  const close = () => act.run(async () => {
    if (!window.confirm(`Close ${sel.title}? The statement and the job review are stored as they stand now and shown that way from then on.`)) return;
    await api.closeMonth(month);
    setReload((n) => n + 1);
  }).catch((e: any) => window.alert(e?.message || 'Could not close the month'));
  const reopen = () => act.run(async () => {
    if (!window.confirm(`Reopen ${sel.title}? The closed statement and job review are deleted and the month shows live figures again.`)) return;
    await api.reopenMonth(month);
    setReload((n) => n + 1);
  }).catch((e: any) => window.alert(e?.message || 'Could not reopen the month'));

  if (error) {
    return (
      <div style={{ padding: '10px 14px', background: C.redTint, border: `1px solid ${C.redSoftLine}`, borderRadius: '8px', color: C.red, fontSize: '13px' }}>
        {error} <button onClick={() => setReload((n) => n + 1)} style={{ ...btn, marginLeft: 8 }}>Retry</button>
      </div>
    );
  }
  if (!list) return <div style={{ color: C.dim, fontSize: '13px' }}>Loading statements…</div>;

  return (
    <div>
      <h3 style={{ fontSize: '11px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', marginBottom: '10px' }}>Month-end statements</h3>

      {/* The picker and what can be done with the month picked. */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
        <select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month"
          style={{ ...btn, fontWeight: 400, minWidth: '170px' }}>
          {months.map((m) => <option key={m.month} value={m.month}>{m.title}{m.status !== 'open' ? ` — ${STATUS[m.status].label}` : ''}</option>)}
        </select>
        <div role="tablist" aria-label="Report" style={{ display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: '7px', overflow: 'hidden' }}>
          {([['statement', 'Statement'], ['jobs', 'Job review']] as const).map(([k, label]) => (
            <button key={k} role="tab" aria-selected={report === k} onClick={() => setReport(k)}
              style={{ ...btn, border: 'none', borderRadius: 0, background: report === k ? C.accentFill : C.panel2, color: report === k ? C.onFill : C.text }}>
              {label}
            </button>
          ))}
        </div>
        <button style={btn} disabled={!sel} onClick={() => openPrint(false)}>View</button>
        <button style={btn} disabled={!sel} onClick={() => openPrint(true)}>Download PDF</button>
        <button style={btn} disabled={!sel || act.pending} onClick={downloadXlsx}>Download Excel</button>
        {sel && sel.status === 'open' && (
          <button
            style={{ ...btn, background: C.accentFill, color: C.onFill, border: 'none', opacity: sel.ended && !list.migrationMissing ? 1 : 0.5, cursor: sel.ended && !list.migrationMissing ? 'pointer' : 'default' }}
            disabled={!sel.ended || list.migrationMissing || act.pending}
            title={list.migrationMissing ? 'Closing a month needs migration 064' : !sel.ended ? `${sel.title} can be closed once it has ended` : undefined}
            onClick={close}
          >
            Close {sel.title}
          </button>
        )}
        {sel && sel.status !== 'open' && <button style={{ ...btn, color: C.red }} disabled={act.pending} onClick={reopen}>Reopen {sel.title}</button>}
      </div>

      {sel?.status === 'changed' && (
        <div style={{ marginBottom: '12px', padding: '10px 12px', background: C.amberTint, border: `1px solid ${C.amberSoftLine}`, borderRadius: '8px', fontSize: '12px', color: C.amber }}>
          <b>Changed since close.</b> {sel.title} shows the figures as closed on {formatDate(String(sel.closedAt).slice(0, 10))}; the live figures now differ:
          <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
            {sel.changes.map((c: any) => (
              <li key={c.label}>{c.label}: {formatMoney(c.closed)} → {formatMoney(c.live)} ({c.delta > 0 ? '+' : ''}{formatMoney(c.delta)})</li>
            ))}
          </ul>
          <div style={{ marginTop: '6px' }}>Reopen and close again to store the current figures.</div>
        </div>
      )}
      {list.migrationMissing && (
        <div style={{ marginBottom: '12px', fontSize: '12px', color: C.muted }}>Closing months needs migration 064; until then every month shows live figures.</div>
      )}

      {/* Every month, with its status. */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: C.dim, fontSize: '10px', textTransform: 'uppercase' }}>
              <th style={{ padding: '8px 12px' }}>Month</th>
              <th style={{ padding: '8px 12px' }}>Status</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Gross income</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Profit to share</th>
              <th style={{ padding: '8px 12px' }}>Closed</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => {
              const st = STATUS[m.status];
              const on = m.month === month;
              return (
                <tr key={m.month} onClick={() => setMonth(m.month)} style={{ cursor: 'pointer', background: on ? C.accentTint : undefined, borderTop: `1px solid ${C.panel2}` }}>
                  <td style={{ padding: '8px 12px', color: C.text, fontWeight: on ? 700 : 500 }}>{m.title}{!m.ended && <span style={{ color: C.dim, fontWeight: 400 }}> · in progress</span>}</td>
                  <td style={{ padding: '8px 12px' }}><span style={{ fontSize: '10px', fontWeight: 700, color: st.color, background: st.bg, padding: '2px 8px', borderRadius: '4px' }}>{st.label}</span></td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: C.mono, color: C.text }}>{formatMoney(m.grossIncome)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: C.mono, color: m.profit < 0 ? C.red : C.text }}>{formatMoney(m.profit)}</td>
                  <td style={{ padding: '8px 12px', color: C.muted }}>{m.closedAt ? `${m.closedByName} · ${formatDate(String(m.closedAt).slice(0, 10))}` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: '10px', color: C.dim, marginTop: '8px' }}>
        The same figures and rules as the rest of Accounting: revenue at invoice, deposits on their payment date, Square's cost as a
        Processor Fee expense, draws out of the partner balance and never out of profit. A closed month is shown as it was closed.
      </div>
    </div>
  );
}
